import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { API_URL } from '../services/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PaymentSuccess = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [downloadCode, setDownloadCode] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    console.log('Session ID from URL:', sessionId);

    if (!sessionId) {
      setError('Invalid access - no session ID');
      return;
    }

    const verifyPayment = async () => {
      const maxRetries = 5;
      const retryDelay = 2000; // 2 seconds
      let attempts = 0;

      const attemptVerification = async () => {
        try {
          console.log(`Verification attempt ${attempts + 1} of ${maxRetries}`);
          const response = await fetch(`${API_URL}/api/stripe/verify-payment/${sessionId}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });
          
          const data = await response.json();
          
          if (response.ok && data.verified && data.downloadCode) {
            setDownloadCode(data.downloadCode);
            setLoading(false);
            return true;
          }
          
          throw new Error(data.message || 'Payment verification failed');
        } catch (err) {
          if (attempts < maxRetries - 1) {
            console.log(`Retrying in ${retryDelay/1000} seconds...`);
            attempts++;
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            return attemptVerification();
          }
          throw err;
        }
      };

      try {
        await attemptVerification();
      } catch (err) {
        console.error('Verification error:', err);
        setError(err.message);
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    verifyPayment();
  }, [location, navigate]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0A0A0A',
          color: 'white'
        }}
      >
        <CircularProgress sx={{ color: '#3EAEFF' }} />
        <Typography sx={{ mt: 2 }}>
          Verifying your payment... Please wait a moment.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0A0A0A',
          color: 'white'
        }}
      >
        <Alert severity="error">{error}</Alert>
        <Typography sx={{ mt: 2 }}>Redirecting to dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#0A0A0A',
        color: 'white',
        p: 3
      }}
    >
      <Box 
        sx={{
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
          p: 4,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.05)'
        }}
      >
        <CheckCircleIcon sx={{ fontSize: 64, color: '#4CAF50', mb: 2 }} />
        <Typography variant="h4" sx={{ mb: 3 }}>
          Payment Successful!
        </Typography>
        <Typography variant="h6" sx={{ mb: 3, color: '#FF6347' }}>
          IMPORTANT: Save this code now!
        </Typography>
        <Typography sx={{ mb: 3 }}>
          This is the only time you'll see this code. Please save it somewhere safe - you'll need it to download your data. 
          The code will also be sent to your email, but make sure to save it now as a backup.
        </Typography>
        <Box 
          sx={{ 
            bgcolor: 'rgba(255,255,255,0.1)',
            p: 2,
            borderRadius: 1,
            mb: 3
          }}
        >
          <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
            {downloadCode}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => navigate('/dashboard')}
          sx={{
            bgcolor: '#3EAEFF',
            '&:hover': { bgcolor: '#2D8CC8' }
          }}
        >
          Return to Dashboard
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentSuccess; 